import { Icon } from './Icon'
import { formatPrice } from '../domain/format'
import { PREMIUM_PRICE_EUR } from '../domain/premium'

/**
 * Says, at the top of the report, that the paid version is what is being shown — and that it is
 * free at the moment.
 *
 * Two things it must not do. It must not read as an advertisement for something the reader has to
 * go and buy, because there is nothing to buy: the price is context for what they already have.
 * And it must not promise content this particular car does not have — a 1975 Beetle with no
 * recalls, no retrofitted equipment and no inspection history would otherwise be told it is
 * receiving four premium sections it cannot see. Hence `sections`, which is the list of premium
 * cards actually rendering below, and hence the caller rendering nothing at all when that list and
 * the premium rows are both empty.
 */
export function PremiumBanner({ sections }: { sections: string[] }) {
  return (
    <aside className="premium-banner">
      <span className="premium-banner__icon">
        <Icon name="unlock" size={26} />
      </span>

      <div>
        <p className="premium-banner__title">Je ziet het volledige Premium-rapport</p>
        <p className="premium-banner__body">
          Alles wat normaal {formatPrice(PREMIUM_PRICE_EUR)} kost, staat hieronder gewoon in het
          rapport. De onderdelen met het{' '}
          <span className="premium-banner__inline-tag">
            <Icon name="unlock" size={12} />
            Premium
          </span>{' '}
          label zijn de betaalde onderdelen{sections.length > 0 ? ': ' : '.'}
          {sections.length > 0 && <PremiumList sections={sections} />}
        </p>
        <p className="premium-banner__note">
          Tijdens de testperiode is dat gratis, zodat we kunnen zien waar de meeste behoefte aan is.
        </p>
      </div>
    </aside>
  )
}

/** "A, B en C" — the Dutch enumeration, in the order the sections appear on the page. */
function PremiumList({ sections }: { sections: string[] }) {
  const last = sections.at(-1) ?? ''
  const rest = sections.slice(0, -1)

  return (
    <>
      <strong>{rest.length > 0 ? `${rest.join(', ')} en ${last}` : last}</strong>
      {/* Two premium cards are titled as questions — "Hoe bijzonder is deze auto?" and "Wat gaat er
          bij dit model vaak mis?" — and either can land last in this list, where a full stop after
          the question mark reads as a typo. */}
      {endsSentence(last) ? '' : '.'}
    </>
  )
}

function endsSentence(title: string): boolean {
  return /[.?!]$/.test(title)
}
